import React, { useCallback, useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, InputLabel, MenuItem, Paper, Select, TextField } from '@mui/material';

import { auth } from 'src/app/database';

import {
  getBrokerData,
  getIsEditProfile,
  isLoadingBrokerDataSelector,
  isLoadingUpdateProfileSelector,
  setIsEditProfile,
  updateProfileData,
} from 'src/store/broker';
import { sendPasswordResetEmail } from 'src/store/app';

import { stateCodes } from 'src/constants/stateCodes';
import { formatPhoneNumber } from 'src/utils/format';

import { UploadImage } from 'src/features/uploadImage/UploadImage';
import { Button, InputPhone, LoaderWrapper, LogoLoader, Status } from 'src/components';

import classes from './brokerProfile.module.scss';

export const BrokerProfile = () => {
  const dispatch = useDispatch();

  const [user] = useAuthState(auth);
  const brokerData = useSelector(getBrokerData);
  const isEditMode = useSelector(getIsEditProfile);
  const isLoadingBrokerData = useSelector(isLoadingBrokerDataSelector);
  const isLoadingUpdateProfile = useSelector(isLoadingUpdateProfileSelector);

  // TODO: Refactor to formData like usage

  const [fullName, setFullName] = useState(user?.displayName);
  const [email, setEmail] = useState(user?.email);
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState(brokerData?.address_zip_code);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [previewImage, setPreviewImage] = useState(brokerData?.photo_url);

  useEffect(() => {
    dispatch(setIsEditProfile(false));
  }, [dispatch]);

  useEffect(() => {
    setZipCode(brokerData?.address_zip_code ?? '');
    setAddress(brokerData?.address_street ?? '');
    setCity(brokerData?.address_city ?? '');
    setState(brokerData?.address_state ?? '');
    setPhoneNumber(brokerData?.phone_number ?? '');
  }, [brokerData]);

  useEffect(() => {
    if (brokerData?.photo_url) {
      setPreviewImage(brokerData?.photo_url);
    }
  }, [brokerData]);

  const onSetPreviewImage = useCallback((image) => {
    setPreviewImage(image);
  }, []);

  const onChangeFullName = useCallback((event) => {
    setFullName(event.target.value);
  }, []);

  const onChangeAddress = useCallback((event) => {
    setAddress(event.target.value);
  }, []);

  const onChangeCity = useCallback((event) => {
    setCity(event.target.value);
  }, []);

  const onChangeState = useCallback((event) => {
    setState(event.target.value);
  }, []);

  const onChangeEmail = useCallback((event) => {
    setEmail(event.target.value);
  }, []);

  const onChangeZipCode = useCallback((event) => {
    if (event.target.value.length > 5) {
      setZipCode(event.target.value.slice(0, 5));
      return;
    }
    setZipCode(event.target.value);
  }, []);

  const onChangePhoneNumber = useCallback((phone) => {
    setPhoneNumber(phone);
  }, []);

  const onClickEditProfile = useCallback(() => {
    dispatch(setIsEditProfile(!isEditMode));
  }, [dispatch, isEditMode]);

  const onSendResetPassword = useCallback(() => {
    dispatch(sendPasswordResetEmail({ email, replacedUrl: '/broker/settings?activeTab=profile' }));
  }, [dispatch, email]);

  const onSaveUpdatedUserProfile = useCallback(() => {
    const payload = {
      email,
      fullName,
      zipCode,
      photo_url: previewImage,
      address_street: address,
      address_city: city,
      address_state: state,
      phone_number: !phoneNumber.includes('+')
        ? formatPhoneNumber(phoneNumber)
        : phoneNumber.replace('+', ''),
    };

    dispatch(updateProfileData(payload));
  }, [dispatch, email, fullName, zipCode, previewImage, address, city, state, phoneNumber]);

  const onCancelEditProfile = useCallback(() => {
    dispatch(setIsEditProfile(false));
    setFullName(user?.displayName);
    setEmail(user?.email);
    setPreviewImage(brokerData?.photo_url || '');
    setAddress(brokerData?.address_street ?? '');
    setCity(brokerData?.address_city ?? '');
    setState(brokerData?.address_state ?? '');
  }, [dispatch, user, brokerData]);

  return (
    <div className={classes.BrokerProfile}>
      <Paper sx={{ padding: 2 }}>
        {isLoadingUpdateProfile || isLoadingBrokerData ? (
          <LoaderWrapper>
            <LogoLoader />
          </LoaderWrapper>
        ) : (
          <div className={classes.BrokerProfileContent}>
            <UploadImage
              path={`users/${user?.uid}/avatar`}
              previewImage={previewImage}
              setPreviewImage={onSetPreviewImage}
              isOnlyImage={!isEditMode}
              type="profile"
              isCirclePreviewImage
            />

            <div className={classes.BrokerProfileGrid}>
              <TextField
                size="small"
                label="Full Name"
                value={fullName}
                onChange={onChangeFullName}
                disabled={!isEditMode}
              />
              <TextField
                size="small"
                label="Email"
                value={email}
                type="email"
                onChange={onChangeEmail}
                disabled={!isEditMode}
              />
              <TextField
                size="small"
                label="Address"
                value={address}
                type="email"
                onChange={onChangeAddress}
                disabled={!isEditMode}
              />
              <TextField
                size="small"
                label="City"
                value={city}
                onChange={onChangeCity}
                disabled={!isEditMode}
              />
              <FormControl sx={{ minWidth: 120 }} size="small">
                <InputLabel id="demo-simple-select-helper-label" size="small">
                  State
                </InputLabel>
                <Select
                  labelId="demo-simple-select-helper-label"
                  value={state}
                  label="State"
                  onChange={onChangeState}
                  size="small"
                  disabled={!isEditMode}
                >
                  {stateCodes?.map((item) => (
                    <MenuItem value={item} key={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Zip Code"
                value={zipCode}
                type="number"
                onChange={onChangeZipCode}
                disabled={!isEditMode}
              />
              <InputPhone
                label="Phone Number"
                value={phoneNumber}
                onChange={onChangePhoneNumber}
                disabled={!isEditMode}
              />

              <TextField
                size="small"
                label="Email Verified"
                value={user?.emailVerified.toString()}
                disabled
              />
              {!isEditMode ? (
                <TextField size="small" label="Password" value="********" disabled />
              ) : (
                <Button title="Change Password" type="primary" onClick={onSendResetPassword} />
              )}
              <div className={classes.BrokerProfileStatus}>
                Subscription Status:
                <Status status={brokerData?.status} />
              </div>
            </div>

            <div className={classes.BrokerProfileFooterButton}>
              {!isEditMode && (
                <Button title="Edit profile" type="secondary" onClick={onClickEditProfile} />
              )}

              {isEditMode && (
                <>
                  <Button title="Save" type="primary" onClick={onSaveUpdatedUserProfile} />
                  <Button title="Cancel" type="secondary" onClick={onCancelEditProfile} />
                </>
              )}
            </div>
          </div>
        )}
      </Paper>
    </div>
  );
};
